import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Store, ChevronDown, Check, Plus, X
} from "lucide-react";
import { auth } from "../config/config-firebase";
import useMerchantStores, {
  getStoreId, isDefaultStoreRow,
} from "../hooks/useMerchantStores";
import { isAdminMerchantMode } from "../utils/adminMerchantMode";

export default function StoreSwitcher() {
  const navigate = useNavigate();
  const [uid, setUid] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      const currentUid = currentUser?.uid || localStorage.getItem("customer_id");
      setUid(currentUid || null);
    });
    return () => unsubscribe();
  }, []);

  // Admin Merchant Mode has one merchant selected via LoginAsMerchant and no
  // real multi-store list to switch between — skip the Firebase-uid-based
  // store hook entirely so it doesn't run against a customer_id it can't
  // resolve.
  const adminMerchantMode = isAdminMerchantMode();
  const {
    stores, numericUserId, switchingId, error, handleSetDefault,
  } = useMerchantStores(adminMerchantMode ? null : uid);

  // Don't render if no stores created yet
  if (!stores.length) return null;

  const activeStore = stores.find(isDefaultStoreRow) || stores[0];

  const handleSwitch = async (store) => {
    const switched = await handleSetDefault(store);
    if (switched) setOpen(false);
  };

  return (
    <>
      {/* ── SWITCHER BANNER ─────────────── */}
      <div
        className="bg-white border-b
          border-gray-100 px-4 py-2.5
          flex items-center justify-between
          gap-3 cursor-pointer
          hover:bg-gray-50 transition-colors"
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ")
            setOpen(true);
        }}
        aria-label="Switch store location"
      >
        <div className="flex items-center
          gap-2.5 min-w-0">
          <div className="w-7 h-7 rounded-lg
            bg-blue-50 flex items-center
            justify-center flex-shrink-0">
            <Store size={14}
              className="text-blue-600" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px]
              text-gray-400 font-medium
              leading-none mb-0.5">
              Current location
            </p>
            <p className="text-sm font-bold
              text-gray-900 truncate">
              {activeStore?.full_name ||
                "Select a store"}
            </p>
          </div>
        </div>
        <div className="flex items-center
          gap-1.5 flex-shrink-0">
          <span className="text-xs text-blue-600
            font-semibold">
            Switch
          </span>
          <ChevronDown size={14}
            className="text-blue-600" />
        </div>
      </div>

      {/* ── STORE PICKER BOTTOM SHEET ───── */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40
            z-50 flex items-end
            sm:items-center sm:justify-center"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white w-full
              sm:max-w-sm rounded-t-3xl
              sm:rounded-2xl px-4 pt-3 pb-8
              shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle */}
            <div className="w-10 h-1
              bg-gray-300 rounded-full
              mx-auto mb-4 sm:hidden" />

            {/* Header */}
            <div className="flex items-center
              justify-between mb-4">
              <h2 className="text-base font-bold
                text-gray-900">
                Switch location
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1"
                aria-label="Close"
              >
                <X size={18}
                  className="text-gray-500" />
              </button>
            </div>

            {error && (
              <p className="text-xs text-red-500
                bg-red-50 border border-red-200
                rounded-xl px-3 py-2 mb-4">
                {error}
              </p>
            )}

            {/* Store list */}
            <div className="space-y-2 mb-4">
              {stores.map((store, index) => {
                const storeId = getStoreId(store);
                const isActive = isDefaultStoreRow(store);
                const isSwitching = switchingId === storeId;
                const disabled =
                  isActive || !storeId || !numericUserId || !!switchingId;
                return (
                  <button
                    key={storeId ?? index}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleSwitch(store)}
                    className={`w-full flex
                      items-center gap-3 px-4
                      py-3 rounded-2xl border
                      transition-colors
                      text-left disabled:opacity-50 ${
                      isActive
                        ? "border-blue-300 bg-blue-50"
                        : "border-gray-200 bg-white hover:bg-gray-50"
                    }`}
                  >
                    <div className={`w-9 h-9
                      rounded-xl flex items-center
                      justify-center flex-shrink-0
                      ${isActive
                        ? "bg-blue-600"
                        : "bg-gray-100"
                      }`}>
                      <Store
                        size={16}
                        className={isActive
                          ? "text-white"
                          : "text-gray-500"
                        }
                      />
                    </div>
                    <div className="flex-1
                      min-w-0">
                      <p className={`text-sm
                        font-semibold truncate ${
                        isActive
                          ? "text-blue-700"
                          : "text-gray-900"
                      }`}>
                        {store.full_name}
                      </p>
                      {store.address && (
                        <p className="text-xs
                          text-gray-400 truncate">
                          {store.address}
                        </p>
                      )}
                    </div>
                    {isActive && (
                      <div className="w-5 h-5
                        rounded-full bg-blue-600
                        flex items-center
                        justify-center
                        flex-shrink-0">
                        <Check size={11}
                          className="text-white" />
                      </div>
                    )}
                    {isSwitching && (
                      <span className="text-[10px]
                        text-blue-500 flex-shrink-0">
                        Switching...
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Add new store shortcut */}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate("/create-store");
              }}
              className="w-full flex items-center
                justify-center gap-2 border-2
                border-dashed border-gray-300
                text-gray-500 text-sm
                font-semibold py-3 rounded-2xl
                hover:border-blue-300
                hover:text-blue-600
                transition-colors"
            >
              <Plus size={15} /> Add new location
            </button>
          </div>
        </div>
      )}
    </>
  );
}
